import { useState } from 'react'
import { withQty } from '../../utils/lineMath'
import { lineKey } from '../../context/CartContext'
import { formatTRY } from '../../utils/money'
import type { OrderLine } from '../../types'

interface Props {
  tableName: string
  /** The ticket as it stands now, after the cashier's corrections. */
  lines: OrderLine[]
  /**
   * How many of each line are new since the ticket was loaded, keyed by line.
   * A quantity rather than a flag: a table that already had one of something
   * and now has two needs one printed, not two.
   */
  addedQty: Map<string, number>
  onConfirm: (lines: OrderLine[]) => void
  onCancel: () => void
}

/**
 * Picks which lines go on the slip that follows a correction.
 *
 * The automatic answer, whatever was added since the ticket was loaded, is
 * right most of the time and is what starts ticked. But a correction is
 * precisely the case where it can be wrong: an item swapped for another leaves
 * the stall needing the new one, and a quantity fixed upward means the tray
 * already holds some of it. So the cashier gets the final say, chosen from the
 * bill as it stands rather than from a diff they cannot see.
 */
export function PrintPickDialog({
  tableName,
  lines,
  addedQty,
  onConfirm,
  onCancel,
}: Props) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(addedQty.keys()))

  const toggle = (key: string) => {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  /**
   * What actually goes on the paper. A line the ticket already held in part is
   * cut down to the difference, the stall has cooked the rest.
   */
  const chosen = lines
    .filter((l) => picked.has(lineKey(l)))
    .map((l) => {
      const qty = addedQty.get(lineKey(l)) ?? l.qty
      return qty === l.qty ? l : withQty(l, qty, 'end')
    })
  const totalKurus = chosen.reduce((sum, l) => sum + l.lineTotalKurus, 0)

  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <div className="dlg__card">
        <h2>{tableName}: fişe yazdır</h2>
        <p className="dlg__empty">
          Tezgaha gidecek ürünleri seçin.
          {addedQty.size > 0
            ? ' Yeni eklenenler işaretli geldi.'
            : ' Bu adisyonun fişi daha önce çıktı; sadece tekrar gerekenleri seçin.'}
        </p>

        <div className="pickl">
          {lines.map((l) => {
            const key = lineKey(l)
            const on = picked.has(key)
            return (
              <button
                key={key}
                type="button"
                className={`pickl__row ${on ? 'is-on' : ''}`}
                onClick={() => toggle(key)}
                aria-pressed={on}
              >
                <span className="pickl__box" aria-hidden="true">
                  {on ? '✓' : ''}
                </span>
                <span className="pickl__name">
                  {l.qty > 1 && <b>{l.qty}× </b>}
                  {l.name}
                  {l.note && <span className="pickl__note">, {l.note}</span>}
                  {/* Says why a line arrived ticked, so an untouched one that
                      is also ticked does not look arbitrary. */}
                  {/* Says how many are new when that is not the whole line,
                      otherwise "yeni" beside 2× reads as both being new. */}
                  {addedQty.has(key) && (
                    <span className="pickl__new">
                      {addedQty.get(key)! < l.qty ? `${addedQty.get(key)} yeni` : 'yeni'}
                    </span>
                  )}
                </span>
                <span className="pickl__total">{formatTRY(l.lineTotalKurus)}</span>
              </button>
            )
          })}
        </div>

        <div className="pickl__sum">
          <span>
            {chosen.length === 0
              ? 'Hiçbiri seçilmedi'
              : `${chosen.reduce((n, l) => n + l.qty, 0)} ürün seçildi`}
          </span>
          <b>{formatTRY(totalKurus)}</b>
        </div>

        <div className="dlg__buttons">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Vazgeç
          </button>
          {/* No "save without printing" here, that is what the other button
              outside this dialog is for. Someone who opened the picker came to
              print; offering the same escape twice only slows them down. */}
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onConfirm(chosen)}
            disabled={chosen.length === 0}
          >
            Kaydet &amp; Yazdır
          </button>
        </div>
      </div>
    </div>
  )
}
